export type SqlSeedTable = {
  name: string;
  columns: string[];
  rows: Record<string, unknown>[];
};

function stripSqlComments(sql: string): string {
  return (sql || '')
    .replace(/--.*$/gm, '')
    .replace(/\/\*[\s\S]*?\*\//g, ' ');
}

function unquoteIdent(raw: string): string {
  const s = raw.trim();
  if (!s) return s;
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith('`') && s.endsWith('`'))) {
    return s.slice(1, -1);
  }
  if (s.startsWith('[') && s.endsWith(']')) return s.slice(1, -1);
  return s;
}

function splitTopLevelByComma(body: string): string[] {
  const parts: string[] = [];
  let cur = '';
  let depth = 0;
  let inSingle = false;

  for (let i = 0; i < body.length; i++) {
    const ch = body[i];
    if (!inSingle && ch === "'") {
      inSingle = true;
      cur += ch;
      continue;
    }
    if (inSingle) {
      if (ch === "'" && body[i + 1] === "'") {
        cur += "''";
        i += 1;
        continue;
      }
      if (ch === "'") inSingle = false;
      cur += ch;
      continue;
    }
    if (ch === '(') depth += 1;
    if (ch === ')') depth = Math.max(0, depth - 1);
    if (depth === 0 && ch === ',') {
      const trimmed = cur.trim();
      if (trimmed) parts.push(trimmed);
      cur = '';
      continue;
    }
    cur += ch;
  }
  const trimmed = cur.trim();
  if (trimmed) parts.push(trimmed);
  return parts;
}

function findMatchingParen(sql: string, openIdx: number): number {
  let depth = 0;
  let inSingle = false;
  for (let i = openIdx; i < sql.length; i++) {
    const ch = sql[i];
    if (!inSingle && ch === "'") {
      inSingle = true;
      continue;
    }
    if (inSingle) {
      if (ch === "'" && sql[i + 1] === "'") {
        i += 1;
        continue;
      }
      if (ch === "'") inSingle = false;
      continue;
    }
    if (ch === '(') depth += 1;
    if (ch === ')') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function parseSqlLiteral(raw: string): unknown {
  const s = raw.trim();
  if (!s || /^null$/i.test(s)) return null;
  if (/^true$/i.test(s)) return true;
  if (/^false$/i.test(s)) return false;
  if (/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(s)) return Number(s);
  if (s.startsWith("'")) {
    let out = '';
    for (let i = 1; i < s.length; i++) {
      if (s[i] === "'" && s[i + 1] === "'") {
        out += "'";
        i += 1;
        continue;
      }
      if (s[i] === "'") break;
      out += s[i];
    }
    return out;
  }
  return unquoteIdent(s);
}

function parseCreateTableColumns(schemaSql: string): Record<string, string[]> {
  const sql = stripSqlComments(schemaSql);
  const byName: Record<string, string[]> = {};
  const createRe =
    /\bcreate\s+(?:temporary\s+|temp\s+)?table\s+(?:if\s+not\s+exists\s+)?(?:(?:"[^"]+"|`[^`]+`|\w+)\s*\.\s*)?(?:"([^"]+)"|`([^`]+)`|(\w+))\s*\(/gi;

  let match: RegExpExecArray | null;
  while ((match = createRe.exec(sql)) !== null) {
    const tableName = unquoteIdent(match[1] || match[2] || match[3] || '');
    const openIdx = sql.indexOf('(', match.index);
    if (!tableName || openIdx < 0) continue;
    const closeIdx = findMatchingParen(sql, openIdx);
    if (closeIdx < 0) continue;
    const columns: string[] = [];
    for (const item of splitTopLevelByComma(sql.slice(openIdx + 1, closeIdx))) {
      const lower = item.trim().toLowerCase();
      if (
        lower.startsWith('primary ') ||
        lower.startsWith('unique ') ||
        lower.startsWith('foreign ') ||
        lower.startsWith('check ') ||
        lower.startsWith('constraint ')
      ) {
        continue;
      }
      const colMatch = item.match(/^\s*(?:"([^"]+)"|`([^`]+)`|\[([^\]]+)\]|(\w+))\b/);
      const colName = unquoteIdent(colMatch?.[1] || colMatch?.[2] || colMatch?.[3] || colMatch?.[4] || '');
      if (colName) columns.push(colName);
    }
    if (columns.length) byName[tableName] = columns;
    createRe.lastIndex = closeIdx + 1;
  }
  return byName;
}

function parseSqlInsertTables(
  seedSql: string,
  schemaColumns: Record<string, string[]>
): SqlSeedTable[] {
  const sql = stripSqlComments(seedSql);
  const byName = new Map<string, SqlSeedTable>();
  const insertRe =
    /\binsert\s+into\s+(?:(?:temporary|temp)\s+)?(?:(?:"[^"]+"|`[^`]+`|\w+)\s*\.\s*)?(?:"([^"]+)"|`([^`]+)`|(\w+))\s*/gi;

  let match: RegExpExecArray | null;
  while ((match = insertRe.exec(sql)) !== null) {
    const tableName = unquoteIdent(match[1] || match[2] || match[3] || '');
    if (!tableName) continue;
    let pos = match.index + match[0].length;
    while (pos < sql.length && /\s/.test(sql[pos])) pos += 1;

    let columns: string[] = [];
    if (sql[pos] === '(') {
      const end = findMatchingParen(sql, pos);
      if (end < 0) continue;
      columns = splitTopLevelByComma(sql.slice(pos + 1, end)).map(unquoteIdent).filter(Boolean);
      pos = end + 1;
      while (pos < sql.length && /\s/.test(sql[pos])) pos += 1;
    }

    if (!/^values\b/i.test(sql.slice(pos))) {
      insertRe.lastIndex = pos;
      continue;
    }
    pos += 'values'.length;

    const tuples: unknown[][] = [];
    while (pos < sql.length) {
      while (pos < sql.length && /[\s,]/.test(sql[pos])) pos += 1;
      if (sql[pos] === ';') break;
      if (sql[pos] !== '(') break;
      const end = findMatchingParen(sql, pos);
      if (end < 0) break;
      tuples.push(splitTopLevelByComma(sql.slice(pos + 1, end)).map(parseSqlLiteral));
      pos = end + 1;
    }

    if (!columns.length) {
      columns =
        schemaColumns[tableName] ||
        tuples[0]?.map((_, i) => `col${i + 1}`) ||
        [];
    }

    const rows = tuples.map((vals) => {
      const rec: Record<string, unknown> = {};
      columns.forEach((col, i) => {
        rec[col] = i < vals.length ? vals[i] : null;
      });
      return rec;
    });

    const existing = byName.get(tableName);
    if (existing) existing.rows.push(...rows);
    else byName.set(tableName, { name: tableName, columns, rows });

    insertRe.lastIndex = pos;
  }

  return Array.from(byName.values());
}

function tablesToPayload(tables: SqlSeedTable[]): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const table of tables) {
    payload[table.name] = {
      __pandas__: 'dataframe',
      columns: table.columns,
      data: table.rows,
    };
  }
  return payload;
}

export type SqlDialect = 'postgres' | 'mysql';

export function pickSqlForTestCase(
  tc:
    | {
        schema_sql?: string;
        schema_sql_mysql?: string;
        seed_sql?: string;
        seed_sql_mysql?: string;
      }
    | undefined,
  dialect: SqlDialect
): { schema?: string; seed?: string } {
  if (!tc) return {};
  if (dialect === 'mysql') {
    return {
      schema: tc.schema_sql_mysql ?? tc.schema_sql,
      seed: tc.seed_sql_mysql ?? tc.seed_sql,
    };
  }
  return { schema: tc.schema_sql, seed: tc.seed_sql };
}

export type SqlTestCaseInputView =
  | { kind: 'tables'; payload: Record<string, unknown> }
  | { kind: 'sql'; text: string }
  | { kind: 'empty' };

export function sqlTestCaseInputView(
  schemaSql: string | undefined,
  seedSql: string | undefined
): SqlTestCaseInputView {
  const schemaColumns = parseCreateTableColumns(schemaSql || '');
  const inserted = parseSqlInsertTables(seedSql || '', schemaColumns);
  const seedText = (seedSql || '').trim();

  if (inserted.length) {
    const tables = [...inserted];
    const seen = new Set(tables.map((t) => t.name));
    for (const [name, columns] of Object.entries(schemaColumns)) {
      if (!seen.has(name)) tables.push({ name, columns, rows: [] });
    }
    return { kind: 'tables', payload: tablesToPayload(tables) };
  }

  if (seedText) return { kind: 'sql', text: seedText };

  const emptyTables = Object.entries(schemaColumns).map(([name, columns]) => ({
    name,
    columns,
    rows: [] as Record<string, unknown>[],
  }));
  if (emptyTables.length) return { kind: 'tables', payload: tablesToPayload(emptyTables) };
  return { kind: 'empty' };
}
